import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useParams } from 'react-router-dom';
import {
  Alert, Button, Checkbox, DataTable, EmptyState, FormSection, Grid, Inline, Link, Menu, Panel, RestrictedState, SaveBar, Segmented, Select, Skeleton, Spinner,
  Stack, t, Text, TextField, toast, Tooltip,
} from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { api, ApiFailure, unwrap } from '../../lib/api';
import { clockTime } from '../../lib/format';
import { useEnvironmentName } from '../../lib/environments';
import { CheckLegend, checkSpec, type DotSpec, healthColumn } from '../../components/StateColumn';
import { FULL_HEIGHT } from '../../lib/tables';
import { changeRef } from '../../components/catalogue/PullRequestNotice';

type MonitoringEnvironment = maintenance.components['schemas']['MonitoringEnvironment'];
type Check = maintenance.components['schemas']['MonitoringCheck'];
type Notification = maintenance.components['schemas']['MonitoringNotification'];
type CheckResult = maintenance.components['schemas']['CheckResult'];
type DraftCheck = Check & { id: string };
type Draft = Record<string, { checks: DraftCheck[]; notification: Notification }>;

/** The interval of a new check: every minute in production (D48: the environment's type, as served). */
const blankCheck = (production: boolean): Check => ({ type: 'https', target: '', expectedStatus: 200, expectedContent: null, timeoutMs: null, interval: production ? 'PT1M' : 'PT5M' });

const draftOf = (environments: MonitoringEnvironment[]): Draft => Object.fromEntries(environments.map(e => [e.environment, {
  checks: e.checks.map(({ id, type, target, expectedStatus, expectedContent, timeoutMs, interval }) => ({ id, type, target, expectedStatus, expectedContent, timeoutMs, interval })),
  notification: e.notification,
}]));
const keyOf = (c: Check) => `${c.type}|${c.target}|${c.expectedStatus ?? ''}|${c.expectedContent ?? ''}|${c.timeoutMs ?? ''}|${c.interval}`;

/** The changes of a draft, counted as the SaveBar says them (a check added or removed, a notification field). */
function changesOf(base: Draft, draft: Draft): number {
  let n = 0;
  for (const [env, d] of Object.entries(draft)) {
    const b = base[env];
    if (!b) continue;
    const before = new Set(b.checks.map(keyOf));
    const after = new Set(d.checks.map(keyOf));
    n += [...after].filter(k => !before.has(k)).length + [...before].filter(k => !after.has(k)).length;
    const bn = b.notification;
    const dn = d.notification;
    if ([...bn.channels].sort().join() !== [...dn.channels].sort().join()) n += 1;
    if (bn.team !== dn.team) n += 1;
    if ((bn.escalation?.after ?? 'none') !== (dn.escalation?.after ?? 'none')) n += 1;
    if (bn.consecutiveFailures !== dn.consecutiveFailures) n += 1;
  }
  return n;
}

/** SPEC-033: the former address of the screen opens the application page's Supervision tab. */
export function MonitoringRedirect() {
  const { application = '' } = useParams();
  return <Navigate to={`/applications/${encodeURIComponent(application)}?tab=monitoring`} replace />;
}

/**
 * « Supervision d'une application » (SPEC-008), the Supervision tab of the
 * application page (SPEC-033, only its address changed): checks per environment
 * with « Tester », who is told and how; saved by pull request to the catalogue,
 * then applied in Checkmk (RG-008-10). The environment is chosen with a
 * Segmented, shared with the page (`&env=`).
 */
export function MonitoringPanel({ application, asked, onEnvironment }: { application: string; asked: string | null; onEnvironment: (env: string) => void }) {
  const queryClient = useQueryClient();
  // D48: an environment shows its name; SMS and the interval follow its type.
  const envLabel = useEnvironmentName();
  const detail = useQuery({
    queryKey: ['monitoring', application],
    queryFn: async () => unwrap(await api.maintenance.GET('/applications/{application}/monitoring', { params: { path: { application } } })),
    refetchInterval: 15_000,
  });
  const data = detail.data;
  const base = useMemo(() => (data ? draftOf(data.environments) : {}), [data]);
  const [draft, setDraft] = useState<Draft>({});
  const [touched, setTouched] = useState(false);
  // A reading of the merged file replaces the draft only while nothing is being edited.
  useEffect(() => {
    if (data && !touched) setDraft(draftOf(data.environments));
  }, [data, touched]);
  const environments = data?.environments.map(e => e.environment) ?? [];
  const isProduction = (id: string) => (data?.environments.find(e => e.environment === id)?.environmentType ?? 'production') === 'production';
  const env = asked && environments.includes(asked) ? asked : environments[0] ?? 'prod';
  const current = draft[env];
  const count = data ? changesOf(base, draft) : 0;

  // D67: an environment without an HTTPS check gets its deployment's address and /health, to change before adding.
  const proposal = (id: string) => (draft[id]?.checks.some(c => c.type === 'https') ? null : data?.environments.find(e => e.environment === id)?.proposedCheck ?? null);
  const proposed = proposal(env);
  const [adding, setAdding] = useState<Check>(blankCheck(isProduction(env)));
  useEffect(() => setAdding(proposal(env) ?? blankCheck(isProduction(env))), [env, data]);
  const [addErrors, setAddErrors] = useState<Record<string, string>>({});
  const [tests, setTests] = useState<Record<string, CheckResult | 'running'>>({});
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const preview = useQuery({
    queryKey: ['monitoring-preview', application, JSON.stringify(draft)],
    queryFn: async () => unwrap(await api.maintenance.POST('/applications/{application}/monitoring/preview', { params: { path: { application } }, body: { environments: strip(draft) } })),
    enabled: Boolean(data?.canUpdate) && count > 0,
  });

  const edit = (fn: (d: Draft) => Draft) => {
    setTouched(true);
    setDraft(d => fn(structuredClone(d)));
  };
  const setNotification = (environment: string, patch: Partial<Notification>) =>
    edit(d => ({ ...d, [environment]: { ...d[environment]!, notification: { ...d[environment]!.notification, ...patch } } }));

  const test = async (environment: string, c: DraftCheck) => {
    setTests(x => ({ ...x, [c.id]: 'running' }));
    try {
      const r = unwrap(await api.maintenance.POST('/checks/test', {
        body: { application, environment, type: c.type, target: c.target, expectedStatus: c.expectedStatus ?? null, expectedContent: c.expectedContent ?? null, timeoutMs: c.timeoutMs ?? null },
      }));
      setTests(x => ({ ...x, [c.id]: r }));
    } catch (e) {
      setTests(x => Object.fromEntries(Object.entries(x).filter(([id]) => id !== c.id)));
      if (e instanceof ApiFailure) toast({ tone: 'neg', title: e.body.message, description: e.body.hint });
    }
  };

  const add = async () => {
    const errors: Record<string, string> = {};
    const check = { ...adding, target: adding.target.trim(), expectedStatus: adding.type === 'https' ? Number(adding.expectedStatus ?? 200) : null, expectedContent: adding.type === 'https' ? adding.expectedContent || null : null };
    // The server's rules decide (SPEC-008 « Cible invalide »); preview answers with the field in error.
    const candidate = { ...current!, checks: [...current!.checks, { ...check, id: `new-${Date.now()}` }] };
    try {
      const r = unwrap(await api.maintenance.POST('/applications/{application}/monitoring/preview', { params: { path: { application } }, body: { environments: strip({ ...draft, [env]: candidate }) } }));
      const index = candidate.checks.length - 1;
      for (const e of r.errors) {
        const m = new RegExp(`^environments\\.${env}\\.checks\\.${index}\\.(\\w+)$`).exec(e.field);
        if (m) errors[m[1]!] = e.message;
      }
    } catch (e) {
      if (e instanceof ApiFailure) toast({ tone: 'neg', title: e.body.message, description: e.body.hint });
      return;
    }
    setAddErrors(errors);
    if (Object.keys(errors).length) return;
    edit(d => ({ ...d, [env]: candidate }));
    setAdding(blankCheck(isProduction(env)));
  };

  const save = async () => {
    setSaving(true);
    try {
      unwrap(await api.maintenance.PUT('/applications/{application}/monitoring', { params: { path: { application } }, body: { environments: strip(draft) } }));
      toast({ tone: 'info', title: t('monitoring.saved') });
      setTouched(false);
      await queryClient.invalidateQueries({ queryKey: ['monitoring', application] });
    } catch (e) {
      if (e instanceof ApiFailure) toast({ tone: 'neg', title: e.body.message, description: e.body.hint });
    } finally {
      setSaving(false);
    }
  };

  const resync = async () => {
    setSyncing(true);
    try {
      const r = unwrap(await api.maintenance.POST('/applications/{application}/monitoring/sync', { params: { path: { application } } }));
      toast({ tone: r.sync.state === 'applied' ? 'pos' : 'neg', title: r.sync.state === 'applied' ? t('monitoring.sync.applied', { time: clockTime(r.sync.at) }) : t('monitoring.sync.failed') });
      await queryClient.invalidateQueries({ queryKey: ['monitoring', application] });
    } catch (e) {
      if (e instanceof ApiFailure) toast({ tone: 'neg', title: e.body.message, description: e.body.hint });
    } finally {
      setSyncing(false);
    }
  };

  // The page is readable; GitHub unreadable is this tab's engine error (SPEC-033 « États »).
  if (detail.error) {
    return <Alert tone="neg" title={detail.error instanceof ApiFailure ? detail.error.body.message : t('errors.retry')}>{detail.error instanceof ApiFailure ? detail.error.body.hint : null}</Alert>;
  }

  const served = data?.environments.find(e => e.environment === env);
  const rows = (current?.checks ?? []).map(c => {
    const saved = served?.checks.find(s => keyOf(s) === keyOf(c));
    return { ...c, state: saved?.state ?? null, output: saved?.output ?? null, lastTest: tests[c.id] ?? saved?.lastTest ?? null, saved: Boolean(saved) };
  });
  type Row = (typeof rows)[number];
  // D53: the state first, a dot only; a check not saved or not yet applied is grey (« En attente » of the legend).
  const waiting = (text: string): DotSpec => ({ ...checkSpec('pending'), label: text });
  const columns = [
    healthColumn<Row>({
      legendLabel: t('server.health.legend'),
      legendTitle: t('server.health.legendTitle'),
      legend: <CheckLegend />,
      spec: c => (!c.saved ? waiting(t('monitoring.unsaved'))
        : !c.state ? waiting(t(data?.sync?.state === 'applied' && data.pullRequest?.state !== 'merged' && data.pullRequest?.state !== 'open' ? 'monitoring.awaitingResult' : 'monitoring.notApplied'))
          : checkSpec(c.state, c.output)),
    }),
    { key: 'type', label: t('monitoring.columns.type'), width: 140, render: (c: Row) => t(`monitoring.types.${c.type}`) },
    { key: 'target', label: t('monitoring.columns.target'), grow: true, mono: true },
    { key: 'expected', label: t('monitoring.columns.expected'), width: 150, mono: true, sortable: false,
      render: (c: Row) => (c.type === 'https' ? [String(c.expectedStatus ?? 200), c.expectedContent].filter(Boolean).join(' · ') : '—') },
    { key: 'interval', label: t('monitoring.columns.interval'), width: 96, render: (c: Row) => t(`monitoring.intervals.${c.interval}`) },
    { key: 'lastTest', label: t('monitoring.columns.lastTest'), width: 280, sortable: false,
      render: (c: Row) => (c.lastTest === 'running'
        ? <Inline gap={8} align="center" wrap={false}><Spinner size={14} label={t('monitoring.testing')} /><Text variant="body-sm">{t('monitoring.testing')}</Text></Inline>
        // The cause can be long (« Échec · code 502 au lieu de 200 · à l'instant »): one line, the full text in the tooltip.
        : c.lastTest ? <Text variant="body-sm" truncate tone={c.lastTest.result === 'failed' ? 'neg' : c.lastTest.result === 'ok' ? undefined : 'mute'}>{c.lastTest.label}</Text>
          : <Text variant="body-sm" tone="mute">{t('monitoring.neverTested')}</Text>) },
    { key: 'actions', label: t('monitoring.columns.actions'), width: 72, sortable: false, pinned: 'right' as const,
      render: (c: Row) => (
        <Menu label={t('monitoring.rowMenu', { target: c.target })} align="end" items={[
          { label: t('monitoring.test'), icon: 'play_arrow', disabled: !data?.canTest || c.lastTest === 'running', onSelect: () => void test(env, c) },
          ...(data?.canUpdate ? [{ separator: true as const }, { label: t('monitoring.remove'), icon: 'delete', danger: true, onSelect: () => edit(d => ({ ...d, [env]: { ...d[env]!, checks: d[env]!.checks.filter(x => x.id !== c.id) } })) }] : []),
        ]} />
      ) },
  ];

  const footer = data?.canUpdate ? (
    <SaveBar
      count={count}
      message={t('monitoring.unsavedCount', { n: count })}
      saveLabel={t('monitoring.save', { n: preview.data?.pullRequests || 1 })}
      cancelLabel={t('monitoring.cancel')}
      saving={saving}
      saveDisabled={Boolean(preview.data?.errors.length) || preview.isFetching}
      onSave={() => void save()}
      onCancel={() => { setTouched(false); setDraft(base); }}
    />
  ) : undefined;

  const pr = data?.pullRequest;
  const sync = data?.sync;
  const notification = current?.notification;
  return (
    <>
      {!data || !current || !notification ? <Stack gap={24}><Panel title={t('monitoring.checks', { environment: '…' })}><Skeleton lines={5} /></Panel><Panel title={t('monitoring.who')}><Skeleton lines={3} /></Panel></Stack> : (
        <Stack gap={24}>
          <Inline gap={12} align="center" wrap>
            <Segmented label={t('monitoring.tabs')} size="sm" value={env} onChange={onEnvironment}
              items={environments.map(id => ({ id, label: envLabel(id), count: draft[id]?.checks.length ?? 0 }))} />
            <Text variant="body-sm" tone="mute">{t('monitoring.subtitle')}</Text>
            {!data.canUpdate && <RestrictedState variant="chip" label={t('monitoring.readOnly')} />}
          </Inline>
          {pr && (pr.state === 'open' || pr.state === 'validation_failed' || (pr.state === 'merged' && sync?.state !== 'failed')) && (
            <Alert tone={pr.state === 'validation_failed' ? 'neg' : 'info'} title={t(`monitoring.pr.${pr.state}`, { ref: changeRef(pr), reviewers: pr.reviewers.join(', ') || t('pullRequest.noReviewer'), message: pr.ciMessage ?? '' })}>
              <Link href={pr.url} external>{t('pullRequest.link', { ref: changeRef(pr) })}</Link>
            </Alert>
          )}
          {sync?.state === 'failed' && (
            <Alert tone="neg" title={t('monitoring.sync.failed')} action={data.canSync ? t('monitoring.sync.retry') : undefined} onAction={data.canSync && !syncing ? () => void resync() : undefined}>
              {sync.error}
            </Alert>
          )}
          {sync?.state === 'applied' && pr?.state === 'applied' && <Alert tone="pos" title={t('monitoring.sync.applied', { time: clockTime(sync.at) })} />}

          <Panel title={t('monitoring.checks', { environment: envLabel(env) })}>
            <Stack gap={16}>
              {rows.length
                ? <DataTable label={t('monitoring.checks', { environment: envLabel(env) })} rows={rows} rowKey="id" toolbar={false} columns={columns} height={FULL_HEIGHT} />
                : <EmptyState icon="monitor_heart" title={t('monitoring.empty')} />}
              {data.canUpdate && (
                <FormSection layout="stacked" headingLevel={3} columns={2}
                  title={proposed ? t('monitoring.add.proposedTitle') : t('monitoring.add.title')}
                  description={proposed ? t('monitoring.add.proposedHint', { path: '/health' }) : undefined}>
                  <Select label={t('monitoring.columns.type')} value={adding.type} onChange={v => setAdding({ ...blankCheck(isProduction(env)), type: (v ?? 'https') as Check['type'], interval: adding.interval })}
                    options={data.options.types.map(value => ({ value, label: t(`monitoring.types.${value}`) }))} />
                  <TextField id="monitoring-target" label={t('monitoring.columns.target')} value={adding.target} mono required hint={t(`monitoring.add.hint.${adding.type}`)} error={addErrors.target}
                    onChange={(e: { target: { value: string } }) => setAdding({ ...adding, target: e.target.value })} />
                  {adding.type === 'https' && (
                    <TextField id="monitoring-status" label={t('monitoring.add.status')} value={String(adding.expectedStatus ?? '')} error={addErrors.expectedStatus}
                      onChange={(e: { target: { value: string } }) => setAdding({ ...adding, expectedStatus: Number(e.target.value) || null })} />
                  )}
                  {adding.type === 'https' && (
                    <TextField id="monitoring-content" label={t('monitoring.add.content')} value={adding.expectedContent ?? ''} mono error={addErrors.expectedContent}
                      onChange={(e: { target: { value: string } }) => setAdding({ ...adding, expectedContent: e.target.value || null })} />
                  )}
                  <Select label={t('monitoring.columns.interval')} value={adding.interval} onChange={v => setAdding({ ...adding, interval: (v ?? adding.interval) as Check['interval'] })}
                    options={data.options.intervals.map(value => ({ value, label: t(`monitoring.intervals.${value}`) }))} error={addErrors.interval} />
                  <Inline gap={8} align="end">
                    <Button variant="secondary" icon="add" disabled={!adding.target.trim()} onClick={() => void add()}>{t('monitoring.add.submit')}</Button>
                  </Inline>
                </FormSection>
              )}
            </Stack>
          </Panel>

          <Panel title={t('monitoring.who')} subtitle={t('monitoring.whoHint')}>
            <Stack gap={16}>
              <DataTable label={t('monitoring.who')} toolbar={false} rowKey="environment" height={FULL_HEIGHT}
                rows={environments.map(e => ({ environment: e, channels: draft[e]?.notification.channels ?? [] }))}
                columns={[
                  { key: 'environment', label: t('monitoring.columns.environment'), grow: true, render: (r: { environment: string }) => envLabel(r.environment) },
                  ...(['email', 'teams', 'sms'] as const).map(way => ({
                    key: way, label: t(`monitoring.ways.${way}`), width: 150, sortable: false,
                    render: (r: { environment: string; channels: string[] }) => {
                      const box = (
                        <Checkbox ariaLabel={t('monitoring.wayFor', { way: t(`monitoring.ways.${way}`), environment: envLabel(r.environment) })}
                          checked={r.channels.includes(way)} disabled={!data.canUpdate || (way === 'sms' && !isProduction(r.environment))}
                          onChange={(checked: boolean) => setNotification(r.environment, { channels: checked ? [...r.channels, way] as Notification['channels'] : r.channels.filter(c => c !== way) as Notification['channels'] })} />
                      );
                      // RG-008-12: SMS for production only.
                      return way === 'sms' && !isProduction(r.environment) ? <Tooltip content={t('monitoring.smsProd')}><span tabIndex={0}>{box}</span></Tooltip> : box;
                    },
                  })),
                ]} />
              <Grid columns="3" gap={16}>
                <Select label={t('monitoring.team', { environment: envLabel(env) })} value={notification.team} disabled={!data.canUpdate} searchable
                  onChange={v => v && setNotification(env, { team: v })} options={data.options.teams.map(value => ({ value, label: value.replace(/^group:default\//, '') }))} />
                <Select label={t('monitoring.escalation')} value={notification.escalation?.after ?? 'none'} disabled={!data.canUpdate}
                  onChange={v => setNotification(env, { escalation: v && v !== 'none' ? { after: v as 'PT15M' | 'PT30M', to: notification.escalation?.to ?? 'ops-lead' } : null })}
                  options={data.options.escalations.map(value => ({ value, label: t(`monitoring.escalations.${value}`) }))} />
                <Select label={t('monitoring.falseAlerts')} value={String(notification.consecutiveFailures)} disabled={!data.canUpdate}
                  onChange={v => setNotification(env, { consecutiveFailures: v === '2' ? 2 : 3 })}
                  options={data.options.consecutiveFailures.map(n => ({ value: String(n), label: t('monitoring.failures', { n }) }))} />
              </Grid>
            </Stack>
          </Panel>
        </Stack>
      )}
      {footer}
    </>
  );
}

/** The draft as the API reads it: the rows' own ids stay on the screen. */
function strip(draft: Draft) {
  return Object.fromEntries(Object.entries(draft).map(([env, d]) => [env, { checks: d.checks.map(({ id: _id, ...c }) => c), notification: d.notification }]));
}

